import React, { useEffect, useMemo, useState } from 'react';
import styles from './Departments.module.scss';
import SearchInput from 'component/atom/forms/input/searchInput/SearchInput';
import Button from 'component/atom/forms/button/Button';
import { useFetch } from 'hooks/useFetch';
import { categoriesEndpoints } from 'hooks/endpoints';
import { useParams } from 'react-router-dom';
import Loader from 'component/atom/loader/Loader';
import CategoryItem from '../CategoryItem';
import AddCategory from '../../addCategory/AddCategory';
import NoData from 'component/molecules/Table/TableComponents/NoData/NoData';

const Departments = () => {
  const { id } = useParams();
  const [search, setSearch] = useState('');
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

  const toggleExpand = (id) => {
    setExpandedCategoryId((prev) => (prev === id ? null : id));
  };

  const {
    data: allCategoriesFull,
    refetch: refetchAll,
    isFetching: isFetchingAll,
  } = useFetch(
    ['allCategoriesFull', id],
    categoriesEndpoints.getAllCategories,
    { enabled: !!id },
    { BotId: id }
  );

  const {
    data: allCategoriesFiltered,
    refetch: refetchFiltered,
    isFetching: isFetchingFiltered,
  } = useFetch(
    ['allCategoriesFiltered', id, search],
    categoriesEndpoints.getAllCategories,
    { enabled: !!id },
    { BotId: id, SearchBy: search }
  );

  const fullItems = allCategoriesFull?.data?.items || [];
  const filteredItems = allCategoriesFiltered?.data?.items || [];

  const isMain = (item) => !item.parentId || item.parentId === ZERO_UUID;

  const getAllSubs = (parentId) =>
    fullItems.filter((i) => i.parentId && i.parentId === parentId);

  const mainsToRender = useMemo(() => {
    if (!search.trim()) {
      return fullItems.filter(isMain);
    }

    const q = search.toLowerCase();
    const mainsMap = new Map();

    fullItems.forEach((item) => {
      if (isMain(item) && (item.categoryText || '').toLowerCase().includes(q)) {
        mainsMap.set(item.categoriesId, item);
      }
    });

    filteredItems.forEach((item) => {
      if (isMain(item)) {
        mainsMap.set(item.categoriesId, item);
      } else if (item.parentId) {
        const parent = fullItems.find((x) => x.categoriesId === item.parentId);
        if (parent && isMain(parent)) mainsMap.set(parent.categoriesId, parent);
      }
    });

    return Array.from(mainsMap.values());
  }, [search, fullItems, filteredItems]);

  const getVisibleSubs = (mainId) => {
    if (!search.trim()) return getAllSubs(mainId);
    const q = search.toLowerCase();
    const fromFiltered = filteredItems.filter((i) => i.parentId === mainId);
    if (fromFiltered.length > 0) return fromFiltered;

    return getAllSubs(mainId).filter((s) =>
      (s.categoryText || '').toLowerCase().includes(q)
    );
  };

  const isAutoExpanded = (mainId) =>
    !!search.trim() && getVisibleSubs(mainId).length > 0;

  useEffect(() => {
    if (!search.trim()) setExpandedCategoryId(null);
  }, [search]);

  const isLoading = isFetchingAll || isFetchingFiltered;

  return (
    <div className={styles.cont}>
      <div className={styles['search-container']}>
        <SearchInput
          name=''
          value={search}
          onChange={(e, newValue) => setSearch(newValue)}
          containerClassName={styles.search}
          classname={styles['search-input']}
          placeholder='Search by department, degree'
        />
        <Button
          labelClassName={styles.addBtn}
          label='New Department'
          onClick={() => {
            setSelectedItem(null);
            setShowAddCategory(true);
          }}
          opicBackground
          type='plus'
        />
      </div>

      {isLoading ? (
        <Loader isComponent />
      ) : (
        <div className={styles.subCont}>
          {mainsToRender?.length > 0 ? (
            mainsToRender.map((item) => {
              const visibleSubs = getVisibleSubs(item.categoriesId);
              const expanded =
                expandedCategoryId === item.categoriesId ||
                isAutoExpanded(item.categoriesId);

              return (
                <CategoryItem
                  key={item.categoriesId}
                  item={item}
                  isExpanded={expanded}
                  toggleExpand={toggleExpand}
                  getSubCategories={getAllSubs}
                  visibleSubCategories={visibleSubs}
                  botId={id}
                  refetch={() => {
                    refetchAll();
                    refetchFiltered();
                  }}
                  setShowAddCategory={setShowAddCategory}
                  setSelectedItem={setSelectedItem}
                />
              );
            })
          ) : (
            <div className={styles['no-data']}>
              <NoData />
            </div>
          )}
        </div>
      )}
      <AddCategory
        isOpen={showAddCategory}
        onClose={() => setShowAddCategory(false)}
        refetch={() => {
          refetchAll();
          refetchFiltered();
        }}
        id={id}
        item={selectedItem}
        setSelectedItem={setSelectedItem}
      />
    </div>
  );
};

export default Departments;
